import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ChatToast } from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';

/**
 * In-app notifications for messages that arrive while the user is elsewhere in Remmi.
 *
 * Sits above the floating Chat button and withdraws on its own after a few seconds. Clicking one
 * opens the widget on that conversation; the × dismisses it without doing so, which is the whole
 * reason the × exists — otherwise the only way to get rid of a toast is to go and read the thread.
 *
 * Rendered by the launcher rather than at the app root, because the launcher is already the
 * component that knows whether chat is enabled for this user and is mounted on every screen.
 */
@Component({
  selector: 'app-chat-toast',
  standalone: true,
  imports: [CommonModule, ChatAvatarComponent],
  templateUrl: './chat-toast.component.html',
  styleUrls: ['./chat-toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatToastComponent {
  readonly store = inject(ChatStore);

  open(toast: ChatToast): void {
    this.store.openFromToast(toast);
  }

  /**
   * `stopPropagation` so the × doesn't also count as a click on the toast body — dismissing one
   * would otherwise open the very conversation the user just declined to look at.
   */
  dismiss(event: MouseEvent, toast: ChatToast): void {
    event.stopPropagation();
    this.store.dismissToast(toast.id);
  }

  trackByToast = (_: number, toast: ChatToast) => toast.id;
}
